import UserPreferenceSingleton from '../helpers/userPreferenceSingleton';
import { MessageDetailsInterface } from '../interfaces/chatMessageInterface';
import LocalDbService, { CONVERSATION_MESSAGE_TABLE_NAME } from './localDbService';
import { captureException } from './logService';

const MAX_MESSAGES_PER_CONVERSATION = 500;

interface ConversationMessageCacheServiceProps {
  organizationId: string;
}

export interface CachedConversation {
  conversationId: string;
  messages: MessageDetailsInterface[];
  cursor: { before: string; beforeId: string } | null;
  hasMore: boolean;
  updatedAt: string | null;
}

interface DbRow {
  id: string;
  userId: string;
  lastAccessed: number;
  value: CachedConversation;
}

let singleton: ConversationMessageCacheService | undefined;
let organizationId: string | undefined;
let lastAccessStamp = 0;

// Strictly increasing, so writes within the same millisecond (e.g. the first-load prefetch) keep
// their order in getRecentConversations.
const nextAccessStamp = () => {
  lastAccessStamp = Math.max(Date.now(), lastAccessStamp + 1);
  return lastAccessStamp;
};

/**
 * Cache of opened conversations: loaded pages + cursor + server sync point, per user,
 * inside the workspace database. Bounded to the newest MAX_MESSAGES_PER_CONVERSATION messages
 * per conversation.
 */
export default class ConversationMessageCacheService extends LocalDbService {
  constructor(props: ConversationMessageCacheServiceProps) {
    const db_name = `${props.organizationId}`;
    const serviceProps = { ...props, dbName: db_name, tableName: CONVERSATION_MESSAGE_TABLE_NAME };
    super(serviceProps);
  }

  static getInstance() {
    const company = UserPreferenceSingleton.getInstance().getWorkspace();
    if (company) {
      if (!singleton || organizationId !== company?.id) {
        organizationId = company?.id;
        singleton = new ConversationMessageCacheService({ organizationId: company?.id });
      }
    }
    return singleton;
  }

  static removeInstance() {
    if (singleton) singleton.closeDb();
    organizationId = undefined;
    singleton = undefined;
  }

  private getRowId(conversationId: string) {
    const userId = UserPreferenceSingleton.getInstance().getCurrentUser()?.id;
    return userId ? { userId, id: `${userId}:${conversationId}` } : null;
  }

  async getConversation(conversationId: string): Promise<CachedConversation | undefined> {
    try {
      const rowId = this.getRowId(conversationId);
      if (!this.db || !rowId) return undefined;
      await this.initializeDatabaseConnection();
      const row: DbRow | undefined = await this.getDb().get(rowId.id);
      if (row) await this.getDb().update(rowId.id, { lastAccessed: nextAccessStamp() });
      return row?.value;
    } catch (e) {
      captureException(e);
      return undefined;
    }
  }

  // Most recently opened conversations of the current user (most recent last), without touching
  // their access time.
  async getRecentConversations(limit: number): Promise<CachedConversation[]> {
    try {
      const userId = UserPreferenceSingleton.getInstance().getCurrentUser()?.id;
      if (!this.db || !userId) return [];
      await this.initializeDatabaseConnection();
      const rows: DbRow[] = await this.getDb().where('userId').equals(userId).sortBy('lastAccessed');
      return rows.slice(-limit).map((row) => row.value);
    } catch (e) {
      captureException(e);
      return [];
    }
  }

  // Any cached conversation for the current user besides `exceptId` (true if the cache can't be read).
  async hasCachedConversations(exceptId?: string | null): Promise<boolean> {
    try {
      const userId = UserPreferenceSingleton.getInstance().getCurrentUser()?.id;
      if (!this.db || !userId) return true;
      await this.initializeDatabaseConnection();
      const count = await this.getDb()
        .where('userId')
        .equals(userId)
        .filter((row: DbRow) => row.value?.conversationId !== exceptId)
        .count();
      return count > 0;
    } catch (e) {
      captureException(e);
      return true;
    }
  }

  async setConversation(conversation: CachedConversation) {
    try {
      const rowId = this.getRowId(conversation.conversationId);
      if (!this.db || !rowId) return;
      await this.initializeDatabaseConnection();
      // IndexedDB only stores structured-cloneable data. Messages can carry class instances with
      // methods (e.g. an optimistic message's sender is a MobX UserModel), so store a plain copy.
      const plain: CachedConversation = JSON.parse(JSON.stringify(conversation));
      let { messages, cursor, hasMore } = plain;
      if (messages.length > MAX_MESSAGES_PER_CONVERSATION) {
        messages = messages.slice(-MAX_MESSAGES_PER_CONVERSATION);
        // Older pages beyond the cap are fetched again from the oldest kept message.
        cursor = { before: messages[0].createdOn, beforeId: messages[0].id };
        hasMore = true;
      }
      const row: DbRow = {
        id: rowId.id,
        userId: rowId.userId,
        lastAccessed: nextAccessStamp(),
        value: { ...plain, messages, cursor, hasMore }
      };
      await this.getDb().put(row);
    } catch (e) {
      captureException(e);
    }
  }
}
